<template>
    <div class="wordcloud-widget">
        <div class="wordcloud-header">
            <h3 class="widget-title">Word Cloud</h3>
            <p v-if="cloud" class="widget-subtitle">{{ cloud.title || 'Share your thoughts!' }}</p>
            <p v-else class="widget-subtitle">Loading...</p>
        </div>
        <!-- Submit Word Form - Only sho            console.log(        'wordcloud.started': (data) => {
            console.log('Wordcloud session started event received:', data)
            isCloudActive.value = true
            error.value = ''
            if (data.cloud) {
                cloud.value = data.cloud
                console.log('Updated cloud from real-time event:', cloud.value)
            }
            // Reload words when wordcloud is activated
            if (cloud.value && cloud.value.id) {
                loadWords()
            }
            // Emit event to parent component to show the widget
            console.log('Emitting session-started to parent')
            emit('session-started', { wordcloud: data.cloud || cloud.value })
        },
        
        'wordcloud.stopped': (data) => {ent received:', data)
            if (cloud.value && data.cloudId === cloud.value.id) {
                if (data.word && data.word.user !== userId) {
                    words.value.push(data.word)
                }
                emit('cloud-updated', {
                    cloudId: data.cloudId,
                    word: data.word
                })
            }
        },
        
        'wordcloud.started': (data) => {is active -->
        <div v-if="isCloudActive && cloud" class="word-form">
            <div class="input-group mb-3">
                <input
                    v-model="wordText"
                    type="text"
                    :placeholder="t('widgets.wordCloud.placeholder')"
                    maxlength="50"
                    class="form-control"
                    :disabled="isSubmitting"
                    @keyup.enter="submitWord"
                />
                <button
                    @click="submitWord"
                    :disabled="!canSubmit"
                    class="btn btn-primary"
                    type="button"
                >
                    {{ isSubmitting ? t('widgets.wordCloud.adding') : t('widgets.wordCloud.add') }}
                </button>
            </div>
            <div class="form-stats">
                <span class="char-count">
                    {{ wordText.length }}/50 {{ t('widgets.wordCloud.characters') }}
                </span>
                <div class="word-stats">
                    <span class="user-words">
                        By me: {{ wordsSubmitted }}
                    </span>
                    <span class="total-words">
                        Total: {{ totalWords }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Word Cloud Visualization - Only shown when shared -->
        <div v-if="cloud && cloud.shared && wordCounts.length > 0" class="word-cloud-display">
            <div class="cloud-header">
                <h4 class="cloud-title">Word Cloud:</h4>
                <div class="layout-controls">
                    <span class="layout-label">{{ t('wordcloud.layoutToggle') }}:</span>
                    <div class="btn-group btn-group-sm" role="group">
                        <button
                            @click="isRadialLayout = false"
                            :class="['btn', !isRadialLayout ? 'btn-primary' : 'btn-outline-primary']"
                        >
                            {{ t('wordcloud.standardLayout') }}
                        </button>
                        <button
                            @click="isRadialLayout = true"
                            :class="['btn', isRadialLayout ? 'btn-primary' : 'btn-outline-primary']"
                        >
                            {{ t('wordcloud.radialLayout') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Standard Grid Layout -->
            <div v-if="!isRadialLayout" class="standard-cloud-layout">
                <div class="word-grid">
                    <span
                        v-for="word in displayedWords"
                        :key="word.text"
                        class="word-item"
                        :style="getWordStyle(word.count)"
                        :title="`${word.text}: ${word.count} submission${word.count !== 1 ? 's' : ''}`"
                    >
                        {{ word.text }}
                    </span>
                </div>
            </div>

            <!-- Radial Cloud Layout -->
            <div v-else class="radial-cloud-container">
                <div class="cloud-background">
                    <svg viewBox="0 0 400 300" class="cloud-border">
                        <!-- Cloud outline -->
                        <path 
                            :d="cloudPath"
                            fill="none" 
                            stroke="#cbd5e1" 
                            stroke-width="2"
                            class="cloud-outline"
                        />
                        <!-- Inner cloud shadow -->
                        <path 
                            :d="cloudPath"
                            fill="rgba(241, 245, 249, 0.6)" 
                            stroke="none"
                            class="cloud-fill"
                        />
                    </svg>
                </div>
                <!-- Words container with proper z-index -->
                <div class="radial-cloud-layout">
                    <span
                        v-for="(word, index) in displayedWords"
                        :key="word.text"
                        class="word-item radial-word"
                        :style="getRadialWordStyle(word.count, index, displayedWords.length)"
                        :title="`${word.text}: ${word.count} submission${word.count !== 1 ? 's' : ''}`"
                    >
                        {{ word.text }}
                    </span>
                </div>
            </div>
            
            <!-- Word List -->
            <div class="word-list">
                <h5 class="word-list-title">Top Words:</h5>
                <div class="word-badges">
                    <span
                        v-for="word in topWords"
                        :key="word.text"
                        class="badge word-badge"
                    >
                        {{ word.text }}
                        <span class="badge-count">{{ word.count }}</span>
                    </span>
                </div>
            </div>
        </div>

        <!-- Message when results are not shared yet -->
        <div v-else-if="cloud && !cloud.shared && wordCounts.length > 0" class="status-message">
            <svg class="status-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <p class="status-title">Results collected but not shared yet</p>
            <p class="status-subtitle">Keep submitting words! Results will be revealed soon.</p>
        </div>

        <!-- Message when no words submitted yet -->
        <div v-else class="status-message">
            <svg class="status-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            <p class="status-title">No words yet. Be the first to contribute!</p>
        </div>

        <div v-if="error" class="alert alert-danger">
            {{ error }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/composables/useApi.js'
import { useWebSocket } from '@/composables/useWebSocket.js'

import { getAudienceConfig, validateConfig } from '@/utils/config.js'
import configjs from '@/config.js'
const { t } = useI18n()

const props = defineProps({
    sessionId: {
        required: true
    },
    // External configuration props
    apiBaseUrl: {
        default: ''
    },
    pusherConfig: {
        default: () => ({})
    },
    authToken: {
        default: null
    },
    congressId: { 
        default: null
    }
})

const emit = defineEmits(['word-submitted', 'cloud-updated', 'session-started', 'session-stopped'])

console.log('🎨🎨🎨 WORDCLOUD WIDGET SCRIPT SETUP EXECUTING 🎨🎨🎨')
console.log('📦 Props received in setup:', props)

// Configuration setup
const config = computed(() => {
    const baseConfig = getAudienceConfig()
    console.log('⚙️ Wordcloud config computed:', { 
        apiBaseUrl: props.apiBaseUrl || baseConfig.apiBaseUrl,
        hasAuthToken: !!(props.authToken || baseConfig.authToken)
    })
    return {
        apiBaseUrl: props.apiBaseUrl || baseConfig.apiBaseUrl,
        authToken: props.authToken || baseConfig.authToken,
        pusher: { ...baseConfig.pusher, ...props.pusherConfig }
    }
})

// Validate configuration
const configValidation = computed(() => validateConfig(config.value))

// Setup API client
const { api } = useApi(config.value)

// Setup WebSocket
const { createEcho, subscribeToChannel } = useWebSocket(config.value)

// Generate unique user ID for this session
const getUserId = () => {
    let userId = sessionStorage.getItem('userId')
    if (!userId) {
        userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
        sessionStorage.setItem('userId', userId)
    }
    return userId
}

// Reactive state
const cloud = ref(null)
const wordText = ref('')
const words = ref([])
const isSubmitting = ref(false)
const error = ref('')
const wordsSubmitted = ref(0)
const userId = getUserId()
const isRadialLayout = ref(false)
const isCloudActive = ref(false)

// Classic cloud shape generation
const cloudPath = computed(() => {
    const width = 400
    const height = 300
    const padding = 10
    
    // Gentle animation
    const time = Date.now() * 0.0008
    const wobble = 8
    
    // Create classic cloud shape
    const leftPuffX = padding + 15
    const leftPuffY = height / 2 + Math.sin(time) * wobble
    
    const topLeftPuffX = width * 0.2 + Math.sin(time + 1) * wobble
    const topLeftPuffY = padding + 15 + Math.cos(time + 1) * wobble
    
    const topCenterPuffX = width / 2 + Math.sin(time + 2) * wobble
    const topCenterPuffY = padding + 8 + Math.cos(time + 2) * wobble
    
    const topRightPuffX = width * 0.8 + Math.sin(time + 3) * wobble
    const topRightPuffY = padding + 12 + Math.cos(time + 3) * wobble
    
    const rightPuffX = width - padding - 15
    const rightPuffY = height * 0.35 + Math.sin(time + 4) * wobble
    
    const bottomRightPuffX = width * 0.85 + Math.sin(time + 5) * wobble
    const bottomRightPuffY = height - padding - 20 + Math.cos(time + 5) * wobble
    
    const bottomCenterPuffX = width * 0.55 + Math.sin(time + 6) * wobble
    const bottomCenterPuffY = height - padding - 15 + Math.cos(time + 6) * wobble
    
    const bottomLeftPuffX = width * 0.25 + Math.sin(time + 7) * wobble
    const bottomLeftPuffY = height - padding - 18 + Math.cos(time + 7) * wobble
    
    // Create smooth cloud outline using curves
    const pathData = `
        M ${leftPuffX} ${leftPuffY}
        C ${leftPuffX - 15} ${leftPuffY - 50}, ${topLeftPuffX - 40} ${topLeftPuffY + 25}, ${topLeftPuffX} ${topLeftPuffY}
        C ${topLeftPuffX + 30} ${topLeftPuffY - 25}, ${topCenterPuffX - 30} ${topCenterPuffY - 15}, ${topCenterPuffX} ${topCenterPuffY}
        C ${topCenterPuffX + 30} ${topCenterPuffY - 20}, ${topRightPuffX - 30} ${topRightPuffY - 15}, ${topRightPuffX} ${topRightPuffY}
        C ${topRightPuffX + 40} ${topRightPuffY - 15}, ${rightPuffX + 20} ${rightPuffY - 40}, ${rightPuffX} ${rightPuffY}
        C ${rightPuffX + 25} ${rightPuffY + 40}, ${bottomRightPuffX + 20} ${bottomRightPuffY - 25}, ${bottomRightPuffX} ${bottomRightPuffY}
        C ${bottomRightPuffX - 25} ${bottomRightPuffY + 20}, ${bottomCenterPuffX + 20} ${bottomCenterPuffY + 15}, ${bottomCenterPuffX} ${bottomCenterPuffY}
        C ${bottomCenterPuffX - 25} ${bottomCenterPuffY + 15}, ${bottomLeftPuffX + 20} ${bottomLeftPuffY + 20}, ${bottomLeftPuffX} ${bottomLeftPuffY}
        C ${bottomLeftPuffX - 40} ${bottomLeftPuffY + 15}, ${leftPuffX - 20} ${leftPuffY + 25}, ${leftPuffX} ${leftPuffY}
        Z
    `.trim().replace(/\s+/g, ' ')
    
    return pathData
})

// Animation management
let cloudAnimationFrame = null
let lastAnimationTime = 0
const animationInterval = 1000 / 30

const startCloudAnimation = () => {
    if (isRadialLayout.value) {
        const animate = (currentTime) => {
            if (currentTime - lastAnimationTime >= animationInterval) {
                cloudPath.value
                lastAnimationTime = currentTime
            }
            cloudAnimationFrame = requestAnimationFrame(animate)
        }
        cloudAnimationFrame = requestAnimationFrame(animate)
    }
}

const stopCloudAnimation = () => {
    if (cloudAnimationFrame) {
        cancelAnimationFrame(cloudAnimationFrame)
        cloudAnimationFrame = null
    }
}

watch(isRadialLayout, (newValue) => {
    if (newValue) {
        startCloudAnimation()
    } else {
        stopCloudAnimation()
    }
})

// Real-time event listeners
let channel = null
let echo = null

const initializePusher = () => {
    if (!props.sessionId) {
        console.warn('WordCloudWidget: No sessionId provided, skipping real-time setup')
        return
    }

    // Get congress ID with proper fallback
    let finalCongressId = props.congressId
    if (!finalCongressId) {
        finalCongressId = configjs.conference || '1657'
    }
    
    finalCongressId = parseInt(finalCongressId)
    if (isNaN(finalCongressId)) {
        console.error('WordCloudWidget: Invalid congressId format, using fallback 1657')
        finalCongressId = 1657
    }
    
    const channelName = `session.${finalCongressId}.${props.sessionId}`
    console.log('WordCloudWidget subscribing to channel:', channelName)
    console.log('WordCloudWidget using congress_id:', finalCongressId)
    
    // Create Echo instance using composable
    echo = createEcho()
    
    // Subscribe to channel with event handlers using composable
    channel = subscribeToChannel(echo, channelName, {
        'word.submitted': (data) => {
            console.log('� Word submitted event received:', data)
            if (cloud.value && data.cloudId === cloud.value.id) {
                if (data.word && data.word.user !== userId) {
                    words.value.push(data.word)
                }
                emit('cloud-updated', {
                    cloudId: data.cloudId,
                    word: data.word
                })
            }
        },
        
        'wordcloud.started': (data) => {
            console.log('Wordcloud session started event received:', data)
            isCloudActive.value = true
            error.value = ''
            if (data.cloud) {
                cloud.value = data.cloud
                console.log('Updated cloud from real-time event:', cloud.value)
            }
            // Reload words when wordcloud is activated
            if (cloud.value && cloud.value.id) {
                loadWords()
            }
            // Emit event to parent component to show the widget
            console.log('Emitting session-started to parent')
            emit('session-started', { wordcloud: data.cloud || cloud.value })
        },
        
        'wordcloud.stopped': (data) => {
            console.log('Wordcloud session stopped event received:', data)
            isCloudActive.value = false
            wordText.value = ''
            error.value = ''
            // Emit event to parent component to hide the widget
            console.log('Emitting session-stopped to parent')
            emit('session-stopped', { wordcloud: data.cloud || cloud.value })
        }
    })
    
    console.log('All event listeners bound successfully')
}

const cleanupPusher = () => {
    if (channel) {
        try {
            channel.stopListening()
            console.log('WordCloudWidget: Channel listeners cleaned up')
        } catch (error) {
            console.log('WordCloudWidget: Channel cleanup handled by composable')
        }
        channel = null
    }
    echo = null
}

// Computed properties
const canSubmit = computed(() => {
    return wordText.value.trim().length > 0 && !isSubmitting.value && isCloudActive.value && cloud.value
})

const wordCounts = computed(() => {
    const counts = {}
    words.value.forEach(word => {
        const text = word.text.toLowerCase().trim()
        if (text) {
            counts[text] = (counts[text] || 0) + 1
        }
    })
    
    return Object.entries(counts)
        .map(([text, count]) => ({ text, count }))
        .sort((a, b) => b.count - a.count)
})

const displayedWords = computed(() => {
    return wordCounts.value.slice(0, 50)
})

const topWords = computed(() => {
    return wordCounts.value.slice(0, 10)
})

const maxCount = computed(() => {
    return wordCounts.value.length > 0 ? wordCounts.value[0].count : 1
})

const totalWords = computed(() => {
    return words.value.length
})

// Methods
const submitWord = async () => {
    if (!canSubmit.value) return
    
    // Check if we have the cloud loaded with an ID
    if (!cloud.value || !cloud.value.id) {
        error.value = 'Wordcloud not loaded. Please wait a moment and try again.'
        return
    }

    const trimmedWord = wordText.value.trim()
    if (!trimmedWord) return

    isSubmitting.value = true
    error.value = ''

    try {
        const response = await api.post(`/api/audience/wordcloud/${cloud.value.id}/words`, {
            text: trimmedWord,
            user_id: userId
        })

        words.value.push(response.data)
        wordText.value = ''
        wordsSubmitted.value++

        emit('word-submitted', {
            cloud_id: cloud.value.id,
            word: response.data
        })

    } catch (err) {
        console.error('Error submitting word:', err)
        
        if (err.response?.status === 429) {
            error.value = 'Please wait before submitting another word.'
        } else if (err.response?.status === 404) {
            error.value = 'This word cloud is no longer active.'
        } else if (err.response?.status === 409) {
            error.value = 'You have already submitted this word to this word cloud.'
        } else {
            error.value = 'Failed to submit word. Please try again.'
        }
    } finally {
        isSubmitting.value = false
    }
}

const getWordStyle = (count) => {
    const minSize = 12
    const maxSize = 32
    const size = minSize + ((count / maxCount.value) * (maxSize - minSize))
    const opacity = 0.4 + (count / maxCount.value) * 0.6
    
    return {
        fontSize: `${size}px`,
        color: `rgba(147, 51, 234, ${opacity})`,
        fontWeight: count > maxCount.value * 0.5 ? 'bold' : 'normal'
    }
}

const getRadialWordStyle = (count, index, totalWords) => {
    if (index === 0) {
        const size = 24 + ((count / maxCount.value) * 32)
        return {
            fontSize: `${size}px`,
            color: `rgba(147, 51, 234, 0.9)`,
            fontWeight: 'bold',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
        }
    }
    
    const minSize = 10
    const maxSize = 24
    const size = minSize + ((count / maxCount.value) * (maxSize - minSize))
    
    const wordsPerRing = [0, 6, 12, 18, 24]
    let ring = 1
    let positionInRing = index - 1
    
    while (ring < wordsPerRing.length && positionInRing >= wordsPerRing[ring]) {
        positionInRing -= wordsPerRing[ring]
        ring++
    }
    
    const angleStep = (2 * Math.PI) / wordsPerRing[ring]
    const angle = positionInRing * angleStep + (ring * 0.3)
    const baseRadius = 60 + (ring - 1) * 45
    const radiusVariation = 15 * Math.sin(angle * 3)
    const radius = baseRadius + radiusVariation
    
    const x = 50 + (radius * Math.cos(angle)) / 3.2
    const y = 50 + (radius * Math.sin(angle)) / 2.4
    
    const clampedX = Math.max(5, Math.min(95, x))
    const clampedY = Math.max(5, Math.min(95, y))
    
    const opacity = 0.5 + (count / maxCount.value) * 0.4
    
    return {
        fontSize: `${size}px`,
        color: `rgba(147, 51, 234, ${opacity})`,
        fontWeight: count > maxCount.value * 0.3 ? '600' : 'normal',
        left: `${clampedX}%`,
        top: `${clampedY}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: Math.floor(5 - ring)
    }
}

// Load wordcloud item from API (SAME AS QANDA/VOTING/FORUM)
const loadCloudItem = async () => {
    try {
        console.log("🔍 Loading Wordcloud item with API baseURL:", config.value.apiBaseUrl)
        const response = await api.get(`/api/sessions/${props.sessionId}/wordcloud`)
        console.log("📦 WORDCLOUD RESPONSE:", response.data)
        
        // API returns array with object containing cloud, words, word_counts, etc.
        const wordcloudData = response.data || []
        if (wordcloudData.length > 0) {
            const cloudData = wordcloudData[0]
            console.log("📦 Extracted cloud data:", cloudData)
            
            // Extract the cloud object and words array from the response
            cloud.value = cloudData.cloud
            console.log("✅ Wordcloud item loaded successfully:", cloud.value)
            console.log("🔍 Wordcloud item ID:", cloud.value.id)
            console.log("🔍 Wordcloud item active status:", cloud.value.active)
            
            isCloudActive.value = cloud.value.active
            
            // Load words from the response (already included in the API response)
            if (cloudData.words && Array.isArray(cloudData.words)) {
                words.value = cloudData.words
                console.log("✅ Loaded words from response:", words.value.length)
                
                // Calculate user's submitted words count
                const userWords = words.value.filter(word => word.user === userId)
                wordsSubmitted.value = userWords.length
                localStorage.setItem(`wordcloud_${cloud.value.id}_submitted`, wordsSubmitted.value.toString())
            }
            
            // Check if wordcloud is active and emit event (SAME AS QANDA/VOTING/FORUM)
            if (cloud.value && cloud.value.id) {
                if (isCloudActive.value) {
                    console.log("✅ Wordcloud is active, ABOUT TO EMIT session-started event")
                    console.log("📤 Emitting with data:", { wordcloud: cloud.value })
                    emit('session-started', { wordcloud: cloud.value })
                    console.log("✅ session-started event EMITTED successfully")
                } else {
                    console.log("ℹ️ Wordcloud exists but is not active")
                    // Don't emit session-stopped - just log it (SAME AS QANDA/VOTING/FORUM)
                }
            } else {
                console.warn("⚠️ Wordcloud item loaded but no ID found:", cloud.value)
            }
        } else {
            console.log("ℹ️ No wordcloud found for this session")
        }
    } catch (err) {
        console.error('❌ Error loading Wordcloud item:', err)
        console.error('📡 API config being used:', { 
            baseURL: config.value.apiBaseUrl,
            fullURL: `${config.value.apiBaseUrl}/api/sessions/${props.sessionId}/wordcloud`
        })
    }
}

const loadWords = async () => {
    if (!cloud.value || !cloud.value.id) {
        console.log("⏳ Cloud item not loaded yet, waiting...")
        return
    }
    
    try {
        console.log("🔍 Loading words with API baseURL:", config.value.apiBaseUrl)
        const response = await api.get(`/api/audience/wordcloud/${cloud.value.id}/words`)
        words.value = response.data.words || []
        
        const userWords = words.value.filter(word => word.user === userId)
        wordsSubmitted.value = userWords.length
        
        localStorage.setItem(`wordcloud_${cloud.value.id}_submitted`, wordsSubmitted.value.toString())
        
        console.log("✅ Words loaded successfully:", words.value.length)
    } catch (err) {
        console.error('❌ Error loading words:', err)
        console.error('📡 API config being used:', { 
            baseURL: config.value.apiBaseUrl,
            fullURL: `${config.value.apiBaseUrl}/api/audience/wordcloud/${cloud.value.id}/words`
        })
    }
}

const saveSubmittedCount = () => {
    if (cloud.value && cloud.value.id) {
        localStorage.setItem(`wordcloud_${cloud.value.id}_submitted`, wordsSubmitted.value.toString())
    }
}

watch(wordsSubmitted, saveSubmittedCount)

// Lifecycle (SAME AS QANDA/VOTING/FORUM)
onMounted(() => {
    if (props.sessionId) {
        console.log('Wordcloud Widget mounted, loading wordcloud item...')
        console.log('Session ID:', props.sessionId)
       
        loadCloudItem() // This will load words after cloudItem is loaded
        initializePusher()
    } else {
        console.warn('Wordcloud Widget mounted without valid sessionId, skipping initialization.')
    }
})

onUnmounted(() => {
    cleanupPusher()
    stopCloudAnimation()
})
</script>

<style scoped>
/* WordCloud Widget Bootstrap Styling */
.wordcloud-widget {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  
    padding: 1.5rem;
    margin-bottom: 1rem;
    box-shadow: 0 4px 12px rgba(111, 66, 193, 0.15);
    animation: slideIn 0.3s ease-out;
}

.wordcloud-header {
    margin-bottom: 1.5rem;
    text-align: center;
}

.widget-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #6f42c1;
    margin-bottom: 0.5rem;
}

.widget-subtitle {
    color: #6c757d;
    margin-bottom: 0;
    font-size: 1rem;
}

/* Form Styling */
.word-form {
    margin-bottom: 1.5rem;
}

.form-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
    font-size: 0.875rem;
}

.char-count {
    color: #6c757d;
}

.word-stats {
    display: flex;
    gap: 1rem;
}

.user-words {
    color: #6f42c1;
    font-weight: 500;
}

.total-words {
    color: #6c757d;
}

/* Cloud Layout Controls */
.cloud-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 1rem;
}

.cloud-title {
    font-weight: 600;
    color: #495057;
    margin: 0;
    font-size: 1.1rem;
}

.layout-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.layout-label {
    font-size: 0.875rem;
    color: #6c757d;
}

/* Word Grid (Standard Layout) */
.standard-cloud-layout {
    margin-bottom: 1.5rem;
}

.word-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-radius: 0.5rem;
    min-height: 200px;
    align-items: center;
    justify-content: center;
    border: 1px solid #dee2e6;
}

/* Radial Cloud Layout */
.radial-cloud-container {
    margin-bottom: 1.5rem;
    position: relative;
    height: 320px;
}

.cloud-background {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 320px;
}

.cloud-border {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
}

.cloud-outline {
    stroke: #cbd5e1;
    stroke-width: 2;
    filter: drop-shadow(0 2px 4px rgba(148, 163, 184, 0.2));
    transition: all 0.3s ease;
}

.cloud-fill {
    fill: rgba(241, 245, 249, 0.6);
    animation: cloudGlow 8s ease-in-out infinite alternate;
}

.radial-cloud-layout {
    position: relative;
    width: 100%;
    height: 320px;
    padding: 2rem;
    z-index: 10;
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
    border-radius: 0.5rem;
}

/* Word Items */
.word-item {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    cursor: default;
    font-weight: 600;
    color: #6f42c1;
    transition: all 0.3s ease;
    animation: wordPop 0.5s ease-out;
}

.word-item:hover {
    transform: scale(1.05);
    background-color: rgba(111, 66, 193, 0.1);
    border-radius: 0.25rem;
}

.radial-word {
    position: absolute;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    animation: radialWordAppear 0.8s ease-out;
}

/* Word List */
.word-list {
    margin-top: 1rem;
    max-height: 8rem;
    overflow-y: auto;
}

.word-list-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
}

.word-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.word-badge {
    background-color: #e9ecef;
    color: lightgrey;
    padding: 0.25rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
}

.badge-count {
    background-color: #6f42c1;
    color: white;
    padding: 0.125rem 0.375rem;
    border-radius: 50%;
    font-size: 0.7rem;
    font-weight: 600;
    min-width: 1.25rem;
    text-align: center;
}

/* Status Messages */
.status-message {
    text-align: center;
    padding: 2rem;
    color: #6c757d;
}

.status-icon {
    width: 3rem;
    height: 3rem;
    margin: 0 auto 1rem;
    color: #6c757d;
}

.status-title {
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: #495057;
}

.status-subtitle {
    font-size: 0.875rem;
    margin: 0;
}

/* Animations */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes wordPop {
    0% {
        opacity: 0;
        transform: scale(0.5);
    }
    50% {
        transform: scale(1.1);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes radialWordAppear {
    0% {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.3) rotate(45deg);
    }
    60% {
        transform: translate(-50%, -50%) scale(1.1) rotate(-15deg);
    }
    100% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1) rotate(0deg);
    }
}

@keyframes cloudGlow {
    0% {
        fill-opacity: 0.4;
        filter: blur(0px);
    }
    100% {
        fill-opacity: 0.8;
        filter: blur(0.5px);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .cloud-header {
        flex-direction: column;
        align-items: stretch;
        text-align: center;
    }
    
    .layout-controls {
        justify-content: center;
    }
    
    .form-stats {
        flex-direction: column;
        gap: 0.5rem;
        text-align: center;
    }
    
    .word-stats {
        justify-content: center;
    }
}
</style>
